const COMMON_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'live.com',
  'msn.com',
  'protonmail.com',
  'comcast.net',
  'att.net',
  'verizon.net',
  'sbcglobal.net',
] as const;

const getThreshold = (inputLength: number, knownLength: number): number =>
  Math.abs(inputLength - knownLength) >= 2 ? 2 : 1;

const optimalStringAlignmentDistance = (a: string, b: string): number => {
  const m = a.length;
  const n = b.length;

  const d: number[][] = Array.from({ length: m + 1 }, () =>
    new Array<number>(n + 1).fill(0)
  );

  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + cost
      );

      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }

  return d[m][n];
};

const extractDomain = (email: string): string | null => {
  const atIndex = email.indexOf('@');
  if (atIndex === -1 || atIndex === email.length - 1) return null;
  return email.slice(atIndex + 1).toLowerCase();
};

export const suggestEmailDomain = (email: string): string | null => {
  const domain = extractDomain(email);
  if (!domain) return null;

  if ((COMMON_DOMAINS as readonly string[]).includes(domain)) return null;

  const localPart = email.slice(0, email.indexOf('@'));

  let bestDomain: string | null = null;
  let bestDistance = Infinity;

  for (const knownDomain of COMMON_DOMAINS) {
    const distance = optimalStringAlignmentDistance(domain, knownDomain);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestDomain = knownDomain;
    }
  }

  if (bestDomain === null) return null;

  const threshold = getThreshold(domain.length, bestDomain.length);
  return bestDistance <= threshold ? `${localPart}@${bestDomain}` : null;
};
